import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BillService } from 'src/app/services/core/services/bills/bill.service';
import { BillPaymentService } from 'src/app/services/core/services/bill-payments/bill-payment.service';
import { ProductService } from 'src/app/services/core/services/product-services/product.service';
import { sweetAlert } from 'src/app/utils/swal';
import { getAmountPaid, getBillPaymentTypeLabel, getPaymentStatus, getRemainingBalance } from 'src/app/utils/common.utils';

@UntilDestroy()

@Component({
  selector: 'app-pay-bill',
  templateUrl: './pay-bill.component.html',
  styleUrls: ['./pay-bill.component.css']
})
export class PayBillComponent implements OnInit {
  payForm!: FormGroup;
  submitted = false;
  billId: any;
  bill: any;
  accounts: any;
  selectedAccount: any;
  selectedCompany: any = '';
  billPayments: any[] = [];
  amountPaid = 0;
  remainingBalance = 0;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private billService: BillService,
    private billPaymentService: BillPaymentService,
    private productService: ProductService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.payForm = this.fb.group({
      VendorRef: this.fb.group({
        name: [''],
        value: ['']
      }),
      TotalAmt: [0, [Validators.required, Validators.min(0.01)]],
      PayType: ['Check'],
      BankAccountRef: this.fb.group({
        name: [''],
        value: ['', Validators.required]
      })
    });

    this.activatedRoute.params.pipe(untilDestroyed(this)).subscribe((params) => {
      this.billId = params['id'];
      if (this.billId) {
        this.getBillById();
      }
    });
  }

  get form() {
    return this.payForm.controls;
  }

  getBillById() {
    this.billService.getBillById(this.billId)
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.bill = data;
          this.amountPaid = getAmountPaid(data);
          this.remainingBalance = getRemainingBalance(data);
          this.payForm.get('TotalAmt')?.setValidators([Validators.required, Validators.min(0.01), Validators.max(this.remainingBalance)]);
          this.selectedCompany = data.company?.id || data.company;
          this.payForm.patchValue({
            VendorRef: {
              name: data.VendorRef?.name || '',
              value: data.VendorRef?.value || ''
            },
            TotalAmt: this.remainingBalance
          });
          if (this.selectedCompany) {
            this.getAccounts();
          }
          this.getBillPayments();
        }
      });
  }

  getBillPayments() {
    this.billPaymentService.getBillPayments({
      bill: this.billId,
      company: this.selectedCompany,
      limit: 9999,
      sortBy: 'createdAt:desc'
    }).pipe(untilDestroyed(this)).subscribe((data: any) => {
      this.billPayments = (data?.[0]?.data || data?.results || []).map((payment: any) => ({
        ...payment,
        typeLabel: getBillPaymentTypeLabel(payment)
      }));
    });
  }

  getAccounts() {
    this.productService.getAccount(this.selectedCompany, { forPayment: 'true' })
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.accounts = Array.isArray(data) ? data : (data.results || []);
          if (this.selectedAccount) {
            this.selectedAccount = this.accounts.find((account: any) => account.Id === this.selectedAccount.Id) || this.selectedAccount;
          }
        }
      });
  }

  onAccountChange(event: Event) {
    const selectedId = (event.target as HTMLSelectElement).value;
    const selectedAccount = this.accounts?.find((account: any) => account.Id === selectedId);
    if (!selectedAccount) return;
    this.selectedAccount = selectedAccount;
    this.payForm.patchValue({
      PayType: selectedAccount.AccountType === 'Credit Card' ? 'CreditCard' : 'Check',
      BankAccountRef: {
        name: selectedAccount.Name,
        value: selectedAccount.Id
      }
    });
  }

  onSave() {
    this.submitted = true;
    if (this.payForm.invalid) return;
    if (!this.bill?.Id) {
      sweetAlert('Error', 'Bill must be synced to QuickBooks before payment', 'error', 'Ok');
      return;
    }

    const payload = {
      ...this.payForm.value,
      bill: this.billId,
      TotalAmt: Number(this.payForm.value.TotalAmt)
    };

    this.billPaymentService.saveBillPayment(this.selectedCompany, payload)
      .pipe(untilDestroyed(this)).subscribe({
        next: (data: any) => {
          if (data) {
                        this.router.navigateByUrl('/bills');
          }
        },
        error: (err) => {
          sweetAlert('Error', err, 'error', 'Ok');
        }
      });
  }

  canPushPayment(payment: any) {
    return !!(payment && !payment.Id && this.bill?.Id);
  }

  canVoidPayment(payment: any) {
    return !!(payment?.Id && getPaymentStatus(this.bill) === 'Paid' && +payment.TotalAmt > 0);
  }

  async onVoidPayment(payment: any) {
    const result = await sweetAlert('Alert', 'Void this payment? The bill balance will be restored.', 'warning', 'Yes', 'No');
    if (!result.value) return;
    this.billPaymentService.voidPayment(this.selectedCompany, payment.id)
      .pipe(untilDestroyed(this)).subscribe({
        next: () => this.getBillById(),
        error: (err) => sweetAlert('Error', err, 'error', 'Ok')
      });
  }

  async onDeletePayment(payment: any) {
    const result = await sweetAlert('Alert', 'Delete this payment? The bill balance will be restored.', 'warning', 'Yes', 'No');
    if (!result.value) return;
    this.billPaymentService.deletePayment(this.selectedCompany, payment.id)
      .pipe(untilDestroyed(this)).subscribe({
        next: () => this.getBillById(),
        error: (err) => sweetAlert('Error', err, 'error', 'Ok')
      });
  }

  async onSyncPayment(payment: any) {
    if (!this.canPushPayment(payment)) return;
    const result = await sweetAlert(
      'Alert',
      'Are you sure you want to overwrite the QuickBooks data with bill payment data',
      'warning',
      'Yes',
      'No'
    );
    if (!result.value) return;

    this.billPaymentService.syncBillPayment(this.selectedCompany, payment.id, { isSyncFrom: false })
      .pipe(untilDestroyed(this)).subscribe({
        next: (data: any) => {
          this.getBillById();
          sweetAlert('Success', data?.message || 'Synced successfully', 'success', 'Ok');
        },
        error: (err) => sweetAlert('Error', err, 'error', 'Ok')
      });
  }
}
